/* global window, React, Button, useEffect, useState */
// ============================================================
// AI Regnskapsfører — sidene som ennå ikke har en egen arbeidsflate
// ============================================================
// Bank, innboks, eiendeler og MVA har hver sin arbeidsflate, skåret til
// godkjenningen de finnes for. De øvrige agentene peker på hele sider —
// fakturalista, kundene, kjøreboka, skattemeldingen. Å sende brukeren dit
// betyr å forlate oversikten hen nettopp åpnet, og komme tilbake med
// nettlesertilbake for å se om raden ble grønn.
//
// Her monteres siden i stedet over AI-siden, med sin egen kode. Det er ikke
// like tilskåret som en dedikert flate, men det holder brukeren på én flate —
// og «Åpne som egen side» står i foten for den som vil hele veien inn.
//
// Sidene tar litt forskjellige props. `year`, `setRoute` og `setYear` dekker
// dem alle; en side som ikke bryr seg om dem, ignorerer dem.
const AP_ROUTE_SURFACES = {
  fakturaliste: 'FakturalistePage',
  'arsavslutning-kontoplan': 'YearClosingChartAccountsPage',
  'arsavslutning-aksjekapital': 'YearClosingShareCapitalPage',
  'arsavslutning-kontanter': 'YearClosingCashCountPage',
  'arsavslutning-mva-kontoer': 'YearClosingVatCleanupPage', 'arsavslutning-pensjon': 'YearClosingPensionPage', 'arsavslutning-kjop': 'YearClosingPurchasesPage', 'arsavslutning-salg': 'YearClosingSalesPage', 'arsavslutning-varelager': 'YearClosingInventoryPage', 'arsavslutning-periodiseringer': 'YearClosingPeriodisationPage',
  'arsavslutning-leverandorgjeld': 'YearClosingPayablesPage', 'arsavslutning-kundefordringer': 'YearClosingReceivablesPage', 'arsavslutning-eiendeler': 'YearClosingFixedAssetsPage', 'arsavslutning-lonn': 'YearClosingPayrollReviewPage', 'arsavslutning-egenkapital': 'YearClosingEquityReviewPage', 'arsavslutning-sluttkontroll': 'YearClosingFinalReviewPage',
  kunder: 'KunderPage',
  produkter: 'ProdukterPage',
  leverandorer: 'LeverandorerPage',
  allebilag: 'AlleBilagPage',
  kjorebok: 'KjorebokPage',
  arsavslutning: 'ArsavslutningPage',
  skattemelding: 'SkattemeldingPage',
  arsregnskap: 'ArsregnskapPage',
  lonn: 'LonnPage',
  // Kontrollpunktene i arsoppgjoret peker paa disse. Uten dem faller
  // «Apne riktig arbeidsomrade» tilbake til full sidenavigasjon, og brukeren
  // mister arsoppgjoret hen stod i.
  fripostering: 'FriPosteringPage',
  bank: 'BankPage',
  innboks: 'InnboksPage',
  innsendinger: 'InnsendingerPage',
  innstillinger: 'InnstillingerPage',
  eiendeler: 'EiendelerPage',
  investeringer: 'InvestmentsPage',
};

const apRouteSurfaceComponent = (route) => {
  const name = AP_ROUTE_SURFACES[route];
  return name ? window[name] || null : null;
};

// Ruter som har fullfort `loadRoute` minst en gang. Modulnivaa-cache i samme
// stil som `routePromises` i pageLoader.js: en arbeidsflate som apnes igjen
// etter forste lasting skal ikke tilbake til "Laster siden..." ved remount.
const apRouteReadyRoutes = new Set();

// Selve monteringen av en side som arbeidsflate. Bade rute-modalen og den
// veiledede arsoppgjorsflyten bruker den, slik at de to ikke kan drive fra
// hverandre paa lasting, feiltilstand og props.
//
// `Page` leses synkront fra `window[componentName]` og kan derfor vaere
// tilstede selv om spraakkatalogen for akkurat denne ruten ikke er ferdig
// hentet (et annet skript kan ha satt det samme globale navnet tidligere).
// Rendring av `Page` gates derfor pa `loadState`, ikke bare pa at
// komponenten finnes — ellers kan siden vise ra oversettelsesnokler.
function ApRouteWorkArea({ route, year, setRoute, setYear, subPage, setSubPage, workAreaContext, onInteract, onComplete, onAdvance }) {
  const [loadState, setLoadState] = useState(() => apRouteReadyRoutes.has(route) ? 'ready' : 'loading');
  useEffect(() => {
    let alive = true;
    const loader = window.PlummePageLoader;
    if (!loader || !loader.loadRoute) { apRouteReadyRoutes.add(route); if (alive) setLoadState('ready'); return; }
    // `loader.hasRoute` ser bort fra skript som feilet forrige gang (den
    // fyller retry-varselet for `loadRoute`, ikke et signal vi kan stole pa
    // her). En rute som ligger igjen i feiltilstand vil derfor rapporteres
    // som "ikke ventende" selv om `loadRoute` under fortsatt ma hente den pa
    // nytt — bruk derfor kun `apRouteReadyRoutes`, egen bekreftet fullfort
    // lasting denne okten, for a starte direkte i "ready".
    if (!apRouteReadyRoutes.has(route)) setLoadState('loading');
    loader.loadRoute(route).then(() => { apRouteReadyRoutes.add(route); if (alive) setLoadState('ready'); }).catch(() => { if (alive) setLoadState('error'); });
    return () => { alive = false; };
  }, [route]);
  const Page = route === 'skattemelding' && workAreaContext?.checkId === 'tax' ? window.YearClosingTaxReview : apRouteSurfaceComponent(route);
  // Siden melder ikke fra selv naar noe endres. En lasting av koen for mye
  // naar flaten lukkes koster lite; tall som lyver koster mer.
  return <div onClickCapture={onInteract}>
    {Page && loadState === 'ready' ? <Page year={year} setRoute={setRoute} setYear={setYear} subPage={subPage} setSubPage={setSubPage} workAreaContext={{ ...(workAreaContext || {}), embedded: true }} onComplete={onComplete} onAdvance={onAdvance} />
      : loadState === 'error' ? <div className="ap-empty">{t('r1b.route.loadFailed')}</div>
      : <div className="ap-empty">{t('r1b.route.loading')}</div>}
  </div>;
}

function ApRouteSurfaceModal({ route, year, task, setYear, onClose, onChanged }) {
  const [changed, setChanged] = useState(false);
  const [surfaceRoute, setSurfaceRoute] = useState(route);
  const [surfaceYear, setSurfaceYear] = useState(task?.incomeYear || year);
  const [surfaceContext, setSurfaceContext] = useState(null);
  const [surfaceBack, setSurfaceBack] = useState(null);
  const annualSurface = ['skattemelding', 'arsavslutning', 'arsregnskap'].includes(surfaceRoute);
  const effectiveYear = annualSurface && task?.incomeYear ? task.incomeYear : surfaceYear;
  // Lønn styres av verten når den har en underfane å styre med. `undefined`
  // betyr «ikke styrt», og siden bruker sin egen startfane — en tom streng
  // ville låst den til ingenting.
  const [subPage, setSubPage] = useState();
  const close = () => { if (changed && onChanged) void onChanged(); onClose(); };
  const routeInside = (next, context = null) => {
    if (next && AP_ROUTE_SURFACES[next]) {
      setSurfaceBack({ route: surfaceRoute, year: surfaceYear, context: surfaceContext, back: surfaceBack });
      setSurfaceRoute(next); setSurfaceContext(context); setChanged(true); return;
    }
    close(); if (next && window.plummeNav) window.plummeNav(next);
  };
  const routeBack = () => {
    if (!surfaceBack) { close(); return; }
    setSurfaceRoute(surfaceBack.route);
    setSurfaceYear(surfaceBack.year);
    setSurfaceContext(surfaceBack.context);
    setSurfaceBack(surfaceBack.back || null);
  };
  const routeAway = (next = surfaceRoute === 'arsavslutning-aksjekapital' ? 'fripostering' : surfaceRoute) => { window.__plummeNextWorkAreaContext = surfaceRoute === 'arsavslutning-aksjekapital' ? { ...(surfaceContext || {}), returnRoute: 'aiautopilot', returnLabel: t('nav.aiBookkeeper', 'AI Regnskapsfører'), returnSurface: { kind: 'year-end', appYear: Number(year) || effectiveYear, task: { incomeYear: effectiveYear, appYear: Number(year) || effectiveYear, assistantStage: 'review', selectedCheckId: surfaceContext?.checkId || 'share' } } } : surfaceContext; if (annualSurface && setYear) setYear(effectiveYear); close(); if (next && window.plummeNav) window.plummeNav(next); };
  const title = window.routeTitle ? window.routeTitle(surfaceRoute) : surfaceRoute;
  return <window.ApSurfaceModal title={title} onClose={close}
    footer={<React.Fragment>
      <Button variant="ghost" onClick={close}>{t('common.close', 'Lukk')}</Button>
      <Button variant="secondary" icon="arrowRight" onClick={() => routeAway()}>{t('ap.yearEnd.openAsPage', 'Åpne som egen side')}</Button>
    </React.Fragment>}>
    {surfaceBack && <div className="ap-year-end-workbar">
      <Button variant="ghost" icon="arrowLeft" onClick={routeBack}>{t('ap.yearEnd.backToWorkArea', 'Tilbake til {{title}}', { title: window.routeTitle ? window.routeTitle(surfaceBack.route) : surfaceBack.route })}</Button>
    </div>}
    <ApRouteWorkArea route={surfaceRoute} year={effectiveYear} setRoute={routeInside} setYear={setSurfaceYear} workAreaContext={surfaceContext}
      subPage={subPage} setSubPage={setSubPage} onInteract={() => setChanged(true)} />
  </window.ApSurfaceModal>;
}

Object.assign(window, { ApRouteSurfaceModal, ApRouteWorkArea, apRouteSurfaceComponent, AP_ROUTE_SURFACES });
